import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import { capitalise } from 'lib/string/stringUtils'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import { mapUrgencyTags, formatOfferName, getCruiseProductType } from './cruiseMap'
import { cruiseInclusionsMap } from './cruiseInclusionMap'
import { sortBy, groupBy } from 'lib/array/arrayUtils'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'

export function cruiseOfferSummaryMap(cruise: CruisesContract.OfferSummaryResponse, regionCode: string): App.CruiseOffer {
  const offerName = formatOfferName(cruise.name)

  const [lowestPrice] = sortBy(cruise.prices, (price) => price.totalAmount, 'asc')
  const lowestDeparturePrice = {
    currencyCode: lowestPrice.currencyCode,
    cabinCategory: capitalise(lowestPrice.cabinType),
    adultFare: lowestPrice.totalAmount,
    total: lowestPrice.totalAmount,
    value: lowestPrice.totalAmount,
    rateCode: lowestPrice.rateCode,
  }

  const mainDepartureDetails = mainDepartureDetailsMap(cruise, regionCode)
  const lowestDeparturePricesByCategory = lowestDeparturePricesByCategoryMap(cruise)
  const departuresSummary = departureSummaryMap(cruise.departures)
  const offerInclusions = cruise.inclusions as Array<App.Cruises.OfferInclusion>
  const hasMemberInclusions = !!mainDepartureDetails.luxPlusInclusionsByTier

  return {
    id: cruise.id,
    productType: getCruiseProductType(cruise),
    cruiseLine: cruiseLineMap(cruise.vendor),
    analyticsType: OFFER_TYPE_CRUISE,
    name: offerName,
    slug: cruise.id,
    destinationDescription: cruise.destinationDescription ?? undefined,
    description: cruise.description ?? undefined,
    disableDeposits: !config.DEPOSITS_ENABLED || !cruise.depositAvailable,
    type: OFFER_TYPE_CRUISE,
    parentType: OFFER_TYPE_CRUISE,
    departures: {},
    departuresSummary,
    duration: cruise.duration,
    departurePort: cruise.port.departure.name,
    departureDates: cruise.availableDepartureDates,
    returnPort: cruise.port.arrival.name,
    ship: shipMap(cruise.ship),
    images: cruise.images.map((image) => ({
      id: image.imageId,
      title: offerName,
    })),
    itinerary: itineraryMap(cruise.itineraries, cruise.cruiseTourItineraries),
    lowestDeparturePricesByCategory,
    lowestDeparturePrice,
    mainDepartureDetails,
    evergreenInclusions: cruise.evergreenInclusions?.map<App.EvergreenInclusion>((serverEvergreenInclusion) => {
      return {
        id: uuidV4(),
        description: serverEvergreenInclusion.inclusion,
        cabinCategories: serverEvergreenInclusion.cabinCategories,
        cabinTypes: serverEvergreenInclusion.cabinTypes,
        shipId: serverEvergreenInclusion.shipId,
      }
    }) ?? [],
    urgencyTags: mapUrgencyTags(),
    luxPlus: {
      hasMemberInclusions,
    },
    offerInclusions,
    loyaltyProductType: cruise.loyaltyProductType,
    campaignTags: cruise.campaignTags ?? [],
    cruiseType: cruise.cruiseType,
  }
}

function departureSummaryMap(departures: CruisesContract.OfferSummaryResponse['departures']) {
  const departuresMap = groupBy(departures, (departure) => departure.departureDate.slice(0, -3))
  return Object.fromEntries(departuresMap.entries()) as Record<string, Array<App.CruiseDepartureSummary>>
}

function cruiseLineMap(cruiseLine: CruisesContract.OfferSummaryResponse['vendor']): App.CruiseLine {
  return {
    id: cruiseLine.id,
    name: cruiseLine.name,
    imageId: cruiseLine.imageId ?? undefined,
  }
}

function shipMap(ship: CruisesContract.OfferSummaryResponse['ship']): App.CruiseShip {
  return {
    id: ship.id,
    name: ship.name,
    images: [],
    shipInfo: [{
      title: 'Ship Info',
      description: ship.description,
      dataTypeName: 'Summary',
    }],
  }
}

function itineraryMap(
  itineraryItems: CruisesContract.OfferSummaryResponse['itineraries'],
  cruiseTourItineraries?: CruisesContract.OfferSummaryResponse['cruiseTourItineraries'],
) {
  if (cruiseTourItineraries && cruiseTourItineraries.length > 0) {
    return cruiseTourItineraries
      .map((cruiseTourItinerary) => {
        const itinerary = itineraryItems.find((item) => item.dayNumber === cruiseTourItinerary.dayNumber)
        return cruiseTourItineraryItemMap(cruiseTourItinerary, itinerary)
      })
      .sort((a, b) => a.startDay - b.startDay)
  }

  return itineraryItems
    .map((itinerary) => itineraryItemMap(itinerary))
    .sort((a, b) => a.startDay - b.startDay)
}

function itineraryItemMap(itinerary: CruisesContract.OfferSummaryResponse['itineraries'][number]) {
  return {
    title: (itinerary.port?.name || itinerary.description || 'At sea').split(',')[0],
    startDay: itinerary.dayNumber,
    description: itinerary.description ?? '',
    departureTime: itinerary.departureTime ?? '',
    startTime: '',
    endTime: itinerary.arrivalTime ?? '',
    port: itinerary.port ? {
      name: itinerary.port.name || '',
      city: itinerary.port.city || '',
      countryCode: itinerary.port.countryCode ?? '',
      nearestAirportCode: '',
      address: '',
      stateProvince: '',
      latitude: itinerary.port.latitude,
      longitude: itinerary.port.longitude,
    } : null,
  }
}

function cruiseTourItineraryItemMap(
  cruiseTourItinerary: NonNullable<CruisesContract.OfferSummaryResponse['cruiseTourItineraries']>[number],
  itinerary?: CruisesContract.OfferSummaryResponse['itineraries'][number],
) {
  const mappedItinerary = itinerary ? itineraryItemMap(itinerary) : null

  if (mappedItinerary) {
    return {
      ...mappedItinerary,
      title: cruiseTourItinerary.description || mappedItinerary.title,
    }
  }

  return {
    title: cruiseTourItinerary.description ?? '',
    startDay: cruiseTourItinerary.dayNumber,
    description: cruiseTourItinerary.description ?? '',
    departureTime: cruiseTourItinerary.departureTime ?? '',
    startTime: '',
    endTime: cruiseTourItinerary.arrivalTime ?? '',
    port: null,
  }
}

function mainDepartureDetailsMap(cruise: CruisesContract.OfferSummaryResponse, regionCode: string): App.CruiseDepartureDetails {
  const [lowestPrice] = sortBy(cruise.prices, (price) => price.totalAmount, 'asc')
  const lowestOverallPriceDetails = lowestOverallPriceDetailsMap(cruise)
  const lowestPriceDetailsByCategory = lowestPriceDetailsByCategoryMap(cruise)
  const offerInclusions = cruise.inclusions as Array<App.Cruises.OfferInclusion>

  const { luxPlusInclusionsByTier, standardInclusions, inclusionDetails } = cruiseInclusionsMap(
    offerInclusions ?? [],
    lowestPrice.departureId,
    lowestOverallPriceDetails.rateCode!,
    regionCode,
    true,
  )

  return {
    id: lowestPrice.departureId,
    lowestOverallPriceDetails,
    lowestPriceDetailsByCategory,
    departureDate: cruise.mainDepartureDate,
    externalId: null,
    arrivalDate: '',
    luxPlusInclusionsByTier,
    standardInclusions,
    inclusionDetails,
  }
}

function lowestOverallPriceDetailsMap(cruise: CruisesContract.OfferSummaryResponse): App.CruisePriceDetails {
  const [lowestPrice] = sortBy(cruise.prices, (price) => price.totalAmount, 'asc')

  const discountPills = lowestPrice.discountPill ? {
    price: lowestPrice.discountPill.totalAmount,
    discountPercentage: lowestPrice.discountPill.percentage,
    discountPrice: lowestPrice.discountPill.discountPrice,
  } : undefined

  return {
    total: lowestPrice.totalAmount,
    value: lowestPrice.totalAmount,
    adultFare: lowestPrice.totalAmount,
    cabinCategory: capitalise(lowestPrice.cabinType),
    isTaxIncluded: true,
    isNccfIncluded: true,
    rateCode: lowestPrice.rateCode ?? undefined,
    currencyCode: lowestPrice.currencyCode,
    discountPills,
  }
}

function lowestDeparturePricesByCategoryMap(cruise: CruisesContract.OfferSummaryResponse): App.LowestDeparturePricesByCategory {
  return cruise.prices.reduce<App.LowestDeparturePricesByCategory>(
    (acc, cur) => {
      const departureId = cur.departureId
      const departurePrices = acc[departureId] || {}
      const cabinType = capitalise(cur.cabinType)

      departurePrices[cabinType] = {
        cabinCategory: cabinType,
        total: cur.totalAmount,
        value: cur.totalAmount,
        adultFare: cur.totalAmount,
        currencyCode: cur.currencyCode,
        rateCode: cur.rateCode,
      }

      return {
        ...acc,
        [departureId]: departurePrices,
      }
    }, {})
}

function lowestPriceDetailsByCategoryMap(cruise: CruisesContract.OfferSummaryResponse): Record<string, App.CruisePriceDetails> {
  return cruise.prices.reduce<Record<string, App.CruisePriceDetails>>((acc, price) => {
    const cabinType = capitalise(price.cabinType)
    const currentLowestByCategory = acc[cabinType]

    if (!currentLowestByCategory || price.totalAmount < currentLowestByCategory.total) {
      acc[cabinType] = {
        total: price.totalAmount,
        value: price.totalAmount,
        adultFare: price.totalAmount,
        isTaxIncluded: true,
        isNccfIncluded: true,
        currencyCode: price.currencyCode,
        rateCode: price.rateCode ?? undefined,
        cabinCategory: cabinType,
      }
    }

    return acc
  }, {})
}
